import React, { useState, useRef, useEffect } from 'react';
import { ExpandMore, PlayArrow } from '@mui/icons-material';
import {
  Typography,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Button,
  Box,
} from '@mui/material';
import { ModalAccordionList } from './modal-accordion-list';
import { routes } from 'src/shared/utils/routes';
import { Lesson } from '../../types/lesson';
import { useAuthFeatures } from '../../hooks/use-auth-features';

interface LessonListProps {
  lessonCount: number;
  title?: string;
  lessons: Partial<Lesson>[];
  courseType?: string;
  courseTitle: string;
  courseDocumentId: string;
  onLessonSelect?: (url: string) => void;
  seasons?: {
    seasonNumber: number;
    lessons: Partial<Lesson>[];
  }[];
}

export const LessonList: React.FC<LessonListProps> = ({
  lessonCount,
  title,
  lessons = [],
  courseType = 'serie',
  courseTitle,
  courseDocumentId,
  onLessonSelect,
  seasons = [],
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const accordionRef = useRef<HTMLDivElement>(null);

  // Find the first incomplete lesson (either in_progress or not_started) across all lessons or seasons
  const findFirstIncompleteLesson = () => {
    // For Series type, check across all seasons
    if (courseType.toLowerCase() === 'serie' && seasons && seasons.length > 0) {
      for (const season of seasons) {
        const incompleteLesson = season.lessons.find((lesson) => {
          const progress = lesson.progress;
          return (
            !progress || progress.status === 'in_progress' || progress.status === 'not_started'
          );
        });
        if (incompleteLesson) return incompleteLesson;
      }
      return null;
    }

    // For other course types, use the flat lessons array
    if (!lessons || lessons.length === 0) return null;

    return lessons.find((lesson) => {
      const progress = lesson.progress;
      return !progress || progress.status === 'in_progress' || progress.status === 'not_started';
    });
  };

  const firstIncompleteLesson = findFirstIncompleteLesson();
  const { isLoggedIn } = useAuthFeatures();
  // Generate URL for lesson based on course type and lesson data
  const generateLessonUrl = (lesson: any) => {
    if (!lesson || !lesson.title || !lesson.documentId) return '';

    if (courseType.toLowerCase() === 'percorsi') {
      return routes.learningPathLesson.makeUrl(
        courseDocumentId,
        courseTitle,
        lesson.title,
        lesson.documentId,
      );
    } else {
      return routes.serieLesson.makeUrl(
        courseDocumentId,
        courseTitle,
        lesson.title,
        lesson.documentId,
      );
    }
  };

  const handlePlayLesson = () => {
    if (firstIncompleteLesson && onLessonSelect) {
      const url = generateLessonUrl(firstIncompleteLesson);
      onLessonSelect(url);
    }
  };

  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);

    if (isExpanded && accordionRef.current) {
      setTimeout(() => {
        accordionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }, 300);
    }
  };

  const renderLessonsList = () => {
    // For Series with seasons, render lessons grouped by seasons
    if (courseType.toLowerCase() === 'serie' && seasons && seasons.length > 0) {
      return (
        <>
          {seasons
            .sort((a, b) => a.seasonNumber - b.seasonNumber)
            .map((season, seasonIndex) => (
              <div key={`season-${seasonIndex}`}>
                <Typography variant="s1" component="h3" color="textPrimary" className="px-2">
                  Stagione {season.seasonNumber}
                </Typography>

                {season.lessons.map((lesson, lessonIndex) => {
                  const lessonUrl = generateLessonUrl(lesson);

                  const isFirstIncomplete =
                    firstIncompleteLesson && lesson.documentId === firstIncompleteLesson.documentId;

                  return (
                    <ModalAccordionList
                      key={`lesson-${lessonIndex}`}
                      title={lesson.title}
                      duration={lesson.duration ?? 0}
                      isLastItem={
                        lessonIndex === season.lessons.length - 1 &&
                        seasonIndex === seasons.length - 1
                      }
                      status={lesson.progress?.status || 'not_started'}
                      progress={lesson.progress?.currentSecond || 0}
                      totalDuration={lesson.duration || 0}
                      isFirstIncomplete={isFirstIncomplete || false}
                      onLessonSelect={onLessonSelect}
                      lessonUrl={lessonUrl}
                    />
                  );
                })}
              </div>
            ))}
        </>
      );
    }

    // For other course types or series without seasons, use the flat lessons list
    return lessons.map((lesson, index) => {
      const lessonUrl = generateLessonUrl(lesson);

      const isFirstIncomplete =
        firstIncompleteLesson && lesson.documentId === firstIncompleteLesson.documentId;

      return (
        <ModalAccordionList
          key={index}
          title={lesson.title}
          duration={lesson.duration ?? 0}
          isLastItem={index === lessons.length - 1}
          status={lesson.progress?.status || 'not_started'}
          progress={lesson.progress?.currentSecond || 0}
          totalDuration={lesson.duration || 0}
          isFirstIncomplete={isFirstIncomplete || false}
          onLessonSelect={onLessonSelect}
          lessonUrl={lessonUrl}
        />
      );
    });
  };

  return (
    <Accordion
      className="w-full mt-4 border-0 shadow-none bg-background rounded-xl"
      ref={accordionRef}
      expanded={expanded}
      onChange={handleAccordionChange}
    >
      <AccordionSummary expandIcon={<ExpandMore color="primary" />}>
        <span className="flex items-center justify-between w-full px-2">
          <Typography variant="s1">Lista lezioni</Typography>
          <Typography variant="body-small-500" className="self-center min-h-[24px]">
            {lessonCount} video
          </Typography>
        </span>
      </AccordionSummary>
      <AccordionDetails>
        {renderLessonsList()}

        {/* {isLoggedIn && firstIncompleteLesson && (
          <div className="px-2 mt-4">
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrow />}
              onClick={handlePlayLesson}
              fullWidth
            >
              <Typography variant="button-m">
                {firstIncompleteLesson.progress?.status === 'in_progress'
                  ? 'Continua a guardare'
                  : 'Inizia a guardare'}
              </Typography>
            </Button>
          </div>
        )} */}
      </AccordionDetails>
    </Accordion>
  );
};
